import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, setToken, type AccountUser } from "./api";

interface AuthCtx {
  user: AccountUser | null;
  loading: boolean;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<AccountUser>;
  register: (email: string, password: string) => Promise<AccountUser>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AccountUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    const token = window.localStorage.getItem("nexkey_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.me();
      setUser(r.data);
    } catch {
      // invalid / expired token — clean up quietly
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthCtx>(() => ({
    user,
    loading,
    isLoggedIn: !!user,
    login: async (email, password) => {
      const r = await api.login(email, password);
      setToken(r.token);
      const me = await api.me();
      setUser(me.data);
      return me.data;
    },
    register: async (email, password) => {
      const r = await api.register(email, password);
      setToken(r.token);
      const me = await api.me();
      setUser(me.data);
      return me.data;
    },
    logout: () => {
      setToken(null);
      setUser(null);
    },
    refresh,
  }), [user, loading, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
